import styled from 'styled-components';
import { FormikErrors, FormikTouched, FormikValues } from 'formik';

import { APPAREL_TYPE_MAP } from 'productTemplate/enums/apparelTypesMap';
import { FitFeedbackFormActions } from 'productTemplate/state/fitFeedbackFormReducer';
import { getFitFeedbackApparelCategoryType } from 'productTemplate/utils/getFitFeedbackApparelCategoryType';
import { QuestionForm } from 'shared/components/QuestionForm';
import { transformSizeOptions } from 'productTemplate/utils/transformSizeOptionsForFitFeedbackForm';
import { useStoreSelector } from 'store/hooks/storeHooks';
import {
  useFitFeedbackFormDispatch,
  useFitFeedbackFormState,
} from 'productTemplate/providers/FitFeedbackProvider';

interface IFitFeedbackFormApparel {
  setFieldValue: (key: string, val: string) => void;
  errors: FormikErrors<{ [key: string]: string }>;
  touched: FormikTouched<FormikValues>;
}

const letterSizeGridStyle = {
  display: 'grid',
  gridGap: '16px',
  gridTemplateColumns: 'repeat(4, 1fr)',
};

export const FitFeedbackFormApparel = ({
  setFieldValue,
  errors,
  touched,
}: IFitFeedbackFormApparel) => {
  const {
    sizeOptions,
    productType,
    name: productName,
  } = useStoreSelector((state) => state.pdp.productTemplate);
  const transformedSizeOptions = transformSizeOptions(sizeOptions);
  const dispatch = useFitFeedbackFormDispatch();
  const { style, sizePurchased, sizeFit } = useFitFeedbackFormState();
  const styleError = touched?.style && errors?.style;
  const sizePurchasedError = touched?.sizePurchased && errors?.sizePurchased;
  const sizeFitError = touched?.sizeFit && errors?.sizeFit;
  const isLetterSize = Number.isNaN(Number(sizeOptions?.[0]?.presentation));
  const productCategoryType = getFitFeedbackApparelCategoryType(productType, productName);

  const apparelType = APPAREL_TYPE_MAP?.[productCategoryType];

  if (!apparelType) {
    return null;
  }

  const handleOnChange = (type) => (e) => {
    const { value, name } = e.target;
    dispatch({ type, payload: value });
    setFieldValue(name, value);
  };

  return (
    <FitFeedbackFormApparel.Wrapper>
      <QuestionForm
        checked={style}
        data-qa="fit_feedback"
        error={styleError}
        gridStyle={{ display: 'grid', gridGap: '16px', gridTemplateColumns: 'repeat(3, 1fr)' }}
        name="style"
        onChange={handleOnChange(FitFeedbackFormActions.setStyle)}
        options={apparelType?.options}
        question={apparelType?.questions?.[0]}
      />
      <FitFeedbackFormApparel.QuestionForm
        checked={sizePurchased}
        data-qa="fit_feedback"
        error={sizePurchasedError}
        gridStyle={isLetterSize ? letterSizeGridStyle : null}
        name="sizePurchased"
        onChange={handleOnChange(FitFeedbackFormActions.setSizePurchased)}
        options={transformedSizeOptions}
        question={apparelType.questions?.[1]}
      />
      <FitFeedbackFormApparel.QuestionForm
        checked={sizeFit}
        data-qa="fit_feedback"
        error={sizeFitError}
        gridStyle={isLetterSize ? letterSizeGridStyle : null}
        name="sizeFit"
        onChange={handleOnChange(FitFeedbackFormActions.setSizeFit)}
        options={transformedSizeOptions}
        question={apparelType?.questions?.[2]}
      />
    </FitFeedbackFormApparel.Wrapper>
  );
};

FitFeedbackFormApparel.Wrapper = styled.div`
  margin-top: 16px;
`;

FitFeedbackFormApparel.QuestionForm = styled(QuestionForm)`
  label {
    text-transform: uppercase;
  }
`;
